import React, { useState, useEffect } from 'react';
import { db } from '../services/db';
import { ShoppingItem, TodoItem } from '../models/types';
import { FaRegCopy, FaRegEnvelope, FaRegComment } from 'react-icons/fa';
import '../styles/ListManager.css';

const ListManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'shopping' | 'todo'>('shopping');
  const [shoppingItems, setShoppingItems] = useState<ShoppingItem[]>([]);
  const [todoItems, setTodoItems] = useState<TodoItem[]>([]);
  const [newItemText, setNewItemText] = useState('');
  const [showCopied, setShowCopied] = useState<boolean>(false);
  
  useEffect(() => {
    loadItems();
  }, []);
  
  const loadItems = async () => {
    try {
      const shopping = await db.getShoppingItems();
      const todos = await db.getTodoItems();
      setShoppingItems(shopping);
      setTodoItems(todos);
    } catch (error) {
      console.error('Error loading list items:', error);
    }
  };
  
  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newItemText.trim()) return;
    
    try {
      if (activeTab === 'shopping') {
        await db.addShoppingItem(newItemText);
      } else {
        await db.addTodoItem(newItemText);
      }
      
      setNewItemText('');
      loadItems();
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };
  
  const handleToggleComplete = async (id: number, currentStatus: boolean) => {
    try {
      const updates = { completed: !currentStatus };
      if (activeTab === 'shopping') {
        await db.updateShoppingItem(id, updates);
      } else {
        await db.updateTodoItem(id, updates);
      }
      
      // Update local state directly for immediate feedback
      if (activeTab === 'shopping') {
        setShoppingItems(prevItems => 
          prevItems.map(item => 
            item.id === id ? { ...item, completed: !currentStatus } : item
          )
        );
      } else {
        setTodoItems(prevItems =>
          prevItems.map(item =>
            item.id === id ? { ...item, completed: !currentStatus } : item
          )
        );
      }
    } catch (error) {
      console.error('Error toggling item completion:', error);
      // Revert UI state on error
      loadItems();
    }
  };
  
  const handleDeleteItem = async (id: number) => {
    try {
      if (activeTab === 'shopping') {
        await db.deleteShoppingItem(id);
      } else {
        await db.deleteTodoItem(id);
      }
      
      loadItems();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };
  
  const getListAsText = (items: Array<ShoppingItem | TodoItem>): string => {
    return items
      .map((item) => {
        const checkbox = item.completed ? '[x]' : '[ ]';
        return `${checkbox} ${item.text}`;
      })
      .join('\n');
  };

  const handleCopyToClipboard = () => {
    const items = activeTab === 'shopping' ? shoppingItems : todoItems;
    const textToCopy = getListAsText(items);
    navigator.clipboard.writeText(textToCopy).then(() => {
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    });
  };

  const handleEmailList = () => {
    const items = activeTab === 'shopping' ? shoppingItems : todoItems;
    const subject = encodeURIComponent(`My ${activeTab === 'shopping' ? 'Shopping' : 'To-Do'} List`);
    const body = encodeURIComponent(getListAsText(items));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleMessageList = () => {
    const items = activeTab === 'shopping' ? shoppingItems : todoItems;
    const text = encodeURIComponent(getListAsText(items));
    window.location.href = `sms:?&body=${text}`;
  };

  const renderItems = () => {
    const items = activeTab === 'shopping' ? shoppingItems : todoItems;
    
    if (items.length === 0) {
      return (
        <div className="empty-list-message">
          No items in your {activeTab === 'shopping' ? 'shopping' : 'to-do'} list.
        </div>
      );
    }
    
    return (
      <ul className="list-items">
        {items.map(item => (
          <li key={item.id} className={`list-item ${item.completed ? 'completed' : ''}`}>
            <input
              type="checkbox"
              checked={item.completed}
              onChange={() => handleToggleComplete(item.id!, item.completed)}
              className="item-checkbox"
            />
            <span className="item-text">{item.text}</span>
            <button
              className="delete-button"
              onClick={() => handleDeleteItem(item.id!)}
              aria-label="Delete item"
            >
              ×
            </button>
          </li>
        ))}
      </ul>
    );
  };
  
  return (
    <div className="list-manager">
      <div className="list-tabs">
        <button
          className={`tab-button ${activeTab === 'shopping' ? 'active' : ''}`}
          onClick={() => setActiveTab('shopping')}
        >
          Shopping List
        </button>
        <button
          className={`tab-button ${activeTab === 'todo' ? 'active' : ''}`}
          onClick={() => setActiveTab('todo')}
        >
          To-Do List
        </button>
      </div>
      
      <div className="list-content">
        <div className="list-actions">
          <button 
            type="button" 
            onClick={handleCopyToClipboard}
            className="list-action-button"
            title="Copy to clipboard"
          >
            <FaRegCopy />
            {showCopied && <span className="copied-tooltip">Copied!</span>}
          </button>
          <button 
            type="button" 
            onClick={handleEmailList}
            className="list-action-button"
            title="Email list"
          >
            <FaRegEnvelope />
          </button>
          <button 
            type="button" 
            onClick={handleMessageList}
            className="list-action-button"
            title="Send as message"
          >
            <FaRegComment />
          </button>
        </div>
        <form onSubmit={handleAddItem} className="add-item-form">
          <input
            type="text"
            value={newItemText}
            onChange={e => setNewItemText(e.target.value)}
            placeholder={`Add ${activeTab === 'shopping' ? 'shopping' : 'to-do'} item...`}
            className="add-item-input"
          />
          <button type="submit" className="add-button">+</button>
        </form>
        
        {renderItems()}
      </div>
    </div>
  );
};

export default ListManager;
